<template>
  <div class="card">

    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          :showAddNew="false"
          title="Opd Bill Report"
      />
    </div>

    <div class="my-2 px-2">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="getOpdBillAnalysis"
      >
        <div class="col-12 col-sm-6 col-md-5 col-lg-4">
          <label for="colFormLabel" class="col-form-label">Consultant</label>
          <v-select
            placeholder="Select Consultant"
            v-model="serviceResourceId"
            :options="consultants"
            label="name"
            :reduce="name => name.id"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="d-flex justify-content-end px-2 mb-2 gap-1">
      <button class="btn btn-primary btn-block waves-effect waves-float waves-light" @click="goToPrint">
        Print
      </button>
      <button class="btn btn-primary btn-block waves-effect waves-float waves-light" @click="exportReport">
        Export
      </button>
    </div>

    <div class="col-12 px-2">
      <ListTable
          :report-list="reportList"
          :specifications="specifications"
          :summary="summary"
          :is-last-page="isLastPage"
      />
    </div>
    <div class="mb-2"></div>

    <div class="px-2 position-absolute bottom-0">
      <Pagination
          :offset="offset"
          @onPageChange="onPageChange"
          ref="pagination"
      />
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {useRoute, useRouter} from 'vue-router';
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import ListTable from '@/components/molecule/company/hospital/hospital-admin/OpdBillAnalysisTable.vue';
import handleHospitalReport from "@/services/modules/hospital/report";
import {computed, inject, onMounted, ref} from "vue";
import Loader from "@/components/atom/LoaderComponent.vue";
import Pagination from "@/components/atom/Pagination.vue";
import {useStore} from "vuex";
import handleCompany from "@/services/modules/company";
import pdfPrinter from '@/services/utils/pdf/opdBillAnalysisPdf';
import handleHospital from "@/services/modules/hospital";
import { resetPaginationPage } from '@/services/utils/global';

const { fetchCompanyInfo } = handleCompany();
const { fetchOpdBillAnalysis } = handleHospitalReport();
const { exportToPDF } = pdfPrinter();
const { fetchServiceResourceList } = handleHospital();

const showError = inject('showError');
const store = useStore();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const companyInfo = ref({})
const reportList = ref([]);
const printList = ref([]);
const specifications = ref({});
const summary = ref(null);
const isLastPage = ref(true);
const consultants = ref([]);
const serviceResourceId = ref(null);
const offset = ref(20);

//computed
const companyId = computed(() => route.params.companyId);
const startDate = computed(() => route.query.start);
const endDate = computed(() => route.query.end);
const page = computed(() => route.query.page);

//methods
const goToPrint = async() => {
  let date = {
    start_date: startDate.value,
    end_date: endDate.value
  }
  let query = getQuery();
  query += '&is_all=true'

  await fetchOpdBillAnalysis(query).then((res) => {
    if (! res.status) {
      printList.value = []
      showError(res.message)
      return
    }
    printList.value = res.data
    summary.value = res.summary
  }).then(() => {
    if (! printList.value.length) return
    return fetchCompanyInfo(companyId.value).then((res) => {
      companyInfo.value = res.data
      exportToPDF(companyInfo.value, printList.value, date, specifications.value)
    })
  })
}

const exportReport = () => {
  if (! reportList.value.length) {
    showError('No data found')
    return
  }
  let token = localStorage.getItem('token');
  let url = `${process.env.VUE_APP_BASE_URL}/export/hospital/opd-bill-analysis${getQuery()}&_token=${token}&is_export=1`;
  let a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
}

const getQuery = () => {
  let query = '?company_id=' + companyId.value

  if (startDate.value) query += '&start_date=' + startDate.value
  if (endDate.value) query += '&end_date=' + endDate.value
  if (serviceResourceId.value) {
    query += '&service_resource_id=' + serviceResourceId.value;
  }
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset.value

  return query
};

const getOpdBillAnalysis = async () => {
  loading.value = true
  await fetchOpdBillAnalysis(getQuery()).then((res) => {
    if(! res.status) {
      reportList.value = []
      summary.value = null
      resetPaginationPage(store, route, router)
      showError(res.message)
      return
    }
    reportList.value = res.data.data;
    specifications.value = res.specifications;
    summary.value = res.summary;
    isLastPage.value = res.data.current_page === res.data.last_page;
    setPagination(res.data)
  }).catch((err) => {
    loading.value = false
  }).finally(() => {
    loading.value = false
  })
};

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
};

const onPageChange = async (pageNumber) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = pageNumber
  await router.push({path: route.path, query: routeQuery})
  await getOpdBillAnalysis()
};

const getConsultants = async () => {
  try {
    const query = '?company_id=' + companyId.value;
    const res = await fetchServiceResourceList(query);
    if (! res.status) return
    consultants.value = res.data;
  } catch (error) {
      showError('Something went wrong');
  }
};

onMounted(async () => {
  await getConsultants();
  await getOpdBillAnalysis();
})
</script>
